import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  /** @type {Animation | null} */
  #animation = null;

  /** Tracks the user's intended open state, separate from details.open during animation. */
  #intent = false;

  /** True in browsers without CSS interpolate-size (e.g., Safari). */
  #needsJsAnimation = !CSS.supports('interpolate-size', 'allow-keywords');

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();
    this.#intent = this.details.open;

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    if (isDesktop && !this.details.open) {
      this.#closeOpenSiblings();
    }

    // Safari/older browsers without interpolate-size: drive the open/close animation in JS.
    if (this.#needsJsAnimation) {
      event.preventDefault();
      this.#intent = !this.#intent;
      this.#animateToIntent();
    }
  };

  /**
   * Closes this accordion row, animating shut on whichever path the browser supports.
   */
  close() {
    this.#intent = false;
    if (this.#needsJsAnimation) {
      this.#animateToIntent();
    } else {
      this.details.open = false;
    }
  }

  /**
   * Closes other open rows in the same accordion container when this one is opting into
   * exclusive-open behaviour (`data-exclusive-desktop` on the closest `.accordion` block).
   */
  #closeOpenSiblings() {
    const container = this.closest('.accordion[data-exclusive-desktop="true"]');
    if (!container) return;

    container.querySelectorAll(':scope > accordion-custom').forEach((sibling) => {
      if (sibling !== this && sibling.details.open) sibling.close();
    });
  }

  /**
   * Animates the details-content open or closed using Web Animations API, matching the timing
   * of the CSS-only path (var(--animation-speed-slow), var(--animation-easing)).
   */
  #animateToIntent() {
    const content = this.details.querySelector('.details-content');
    if (!(content instanceof HTMLElement)) {
      this.details.open = this.#intent;
      return;
    }

    // Capture the live rendered height before cancelling any in-flight animation.
    const currentRect = content.getBoundingClientRect();
    const currentHeight = currentRect.height;
    this.#animation?.cancel();

    const rootStyles = getComputedStyle(document.documentElement);
    const duration = this.#parseTimeMs(rootStyles.getPropertyValue('--animation-speed-slow')) || 500;
    const easing = rootStyles.getPropertyValue('--animation-easing').trim() || 'ease';

    if (this.#intent) {
      // Opening: render content (so we can measure full height), then animate from current → full.
      this.details.open = true;
      // Reading scrollHeight here forces layout with content rendered.
      const targetHeight = content.scrollHeight;
      const targetStyles = getComputedStyle(content);
      const targetPaddingTop = targetStyles.paddingTop;
      const targetPaddingBottom = targetStyles.paddingBottom;

      this.#animation = content.animate(
        [
          { height: `${currentHeight}px`, opacity: currentHeight === 0 ? 0 : 1, paddingTop: '0px', paddingBottom: '0px' },
          { height: `${targetHeight}px`, opacity: 1, paddingTop: targetPaddingTop, paddingBottom: targetPaddingBottom },
        ],
        { duration, easing, fill: 'none' }
      );

      this.#animation.finished.then(() => { this.#animation = null; }).catch(() => {});
    } else {
      // Closing: animate from current → 0, then set details.open = false.
      this.#animation = content.animate(
        [
          { height: `${currentHeight}px`, opacity: 1 },
          { height: '0px', opacity: 0, paddingTop: '0px', paddingBottom: '0px' },
        ],
        { duration, easing, fill: 'forwards' }
      );

      this.#animation.finished.then(() => {
        this.details.open = false;
        this.#animation?.cancel();
        this.#animation = null;
      }).catch(() => {});
    }
  }

  /**
   * Parses a CSS time value (e.g., "500ms", "0.5s") to milliseconds.
   * @param {string} value
   * @returns {number}
   */
  #parseTimeMs(value) {
    const trimmed = value.trim();
    if (trimmed.endsWith('ms')) return parseFloat(trimmed);
    if (trimmed.endsWith('s')) return parseFloat(trimmed) * 1000;
    return parseFloat(trimmed) || 0;
  }

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
    this.#intent = this.details.open;
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown = (event) => {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      if (this.#needsJsAnimation && this.#intent) {
        this.#intent = false;
        this.#animateToIntent();
      } else {
        this.details.open = false;
      }
      this.summary.focus();
    }
  };
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
